import { ReactNode, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Menu, X, MapPin, Phone, Mail } from 'lucide-react';
import { cn } from '../lib/cn';
import { api } from '../lib/api';

interface Organization {
  id: string;
  name: string;
  logoUrl: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  website: string | null;
}

interface NavPage {
  id: string;
  title: string;
  slug: string;
  navOrder: number;
  parentId: string | null;
}

interface LayoutProps {
  children: ReactNode;
  org: Organization | undefined;
  currentPage: string;
  onNavigate: (page: string) => void;
  isBuiltInPage?: boolean;
}

const builtInNavigation = [
  { name: 'Home', page: 'home' },
  { name: 'Events', page: 'events' },
  { name: 'About', page: 'about' },
  { name: 'Contact', page: 'contact' },
];

export function Layout({ children, org, currentPage, onNavigate, isBuiltInPage: _isBuiltInPage }: LayoutProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Fetch dynamic navigation pages from API
  const { data: dynamicNavPages } = useQuery({
    queryKey: ['public-navigation'],
    queryFn: async () => {
      const response = await api.get<NavPage[]>('/pages/public/navigation');
      if (!response.success) return [];
      return response.data || [];
    },
    staleTime: 1000 * 60 * 5,
  });

  // Build the full navigation: built-in pages + dynamic pages
  // Filter out dynamic pages whose slugs match built-in page names to avoid duplicates
  const builtInSlugs = new Set(builtInNavigation.map((n) => n.page));
  const dynamicItems = (dynamicNavPages || [])
    .filter((p) => !builtInSlugs.has(p.slug))
    .sort((a, b) => a.navOrder - b.navOrder)
    .map((p) => ({ name: p.title, page: p.slug }));

  const navigation = [...builtInNavigation, ...dynamicItems];

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white shadow-sm">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            {/* Logo */}
            <button
              onClick={() => onNavigate('home')}
              className="flex items-center gap-3"
            >
              {org?.logoUrl ? (
                <img
                  src={org.logoUrl}
                  alt={org.name}
                  className="h-10 w-10 rounded-full object-cover"
                />
              ) : (
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-600 text-white font-bold text-lg">
                  {org?.name?.charAt(0) || 'S'}
                </div>
              )}
              <span className="text-xl font-semibold text-gray-900 hidden sm:block">
                {org?.name || 'Synagogue'}
              </span>
            </button>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-1">
              {navigation.map((item) => (
                <button
                  key={item.page}
                  onClick={() => onNavigate(item.page)}
                  className={cn(
                    'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                    currentPage === item.page
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  )}
                >
                  {item.name}
                </button>
              ))}
              <a
                href={import.meta.env.VITE_PORTAL_URL || '/portal'}
                className="ml-4 px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors"
              >
                Member Login
              </a>
            </nav>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-gray-200 bg-white">
            <nav className="px-4 py-3 space-y-1">
              {navigation.map((item) => (
                <button
                  key={item.page}
                  onClick={() => {
                    onNavigate(item.page);
                    setMobileMenuOpen(false);
                  }}
                  className={cn(
                    'w-full text-left px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                    currentPage === item.page
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  )}
                >
                  {item.name}
                </button>
              ))}
              <a
                href={import.meta.env.VITE_PORTAL_URL || '/portal'}
                className="block w-full text-center mt-2 px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium"
              >
                Member Login
              </a>
            </nav>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="flex-1">{children}</main>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <div className="grid gap-8 md:grid-cols-3">
            {/* Organization Info */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">
                {org?.name || 'Our Synagogue'}
              </h3>
              {org?.address && (
                <div className="flex items-start gap-2 text-sm mb-2">
                  <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>
                    {org.address}
                    {org.city && `, ${org.city}`}
                    {org.state && `, ${org.state}`}
                    {org.postalCode && ` ${org.postalCode}`}
                  </span>
                </div>
              )}
              {org?.phone && (
                <div className="flex items-center gap-2 text-sm mb-2">
                  <Phone className="h-4 w-4" />
                  <a href={`tel:${org.phone}`} className="hover:text-white">
                    {org.phone}
                  </a>
                </div>
              )}
              {org?.email && (
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="h-4 w-4" />
                  <a href={`mailto:${org.email}`} className="hover:text-white">
                    {org.email}
                  </a>
                </div>
              )}
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Quick Links</h3>
              <ul className="space-y-2 text-sm">
                {navigation.map((item) => (
                  <li key={item.page}>
                    <button
                      onClick={() => onNavigate(item.page)}
                      className="hover:text-white transition-colors"
                    >
                      {item.name}
                    </button>
                  </li>
                ))}
                <li>
                  <a href={import.meta.env.VITE_PORTAL_URL || '/portal'} className="hover:text-white transition-colors">
                    Member Portal
                  </a>
                </li>
              </ul>
            </div>

            {/* Powered By */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Powered By</h3>
              <a
                href="https://github.com/dk0hn/openshul"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 hover:opacity-80 transition-opacity"
              >
                <img src="/logo.svg" alt="OpenShul" className="h-8 w-8" />
                <span className="text-sm font-semibold text-white">OpenShul</span>
              </a>
              <p className="text-xs mt-2 text-gray-400">
                Open source synagogue management
              </p>
            </div>
          </div>

          <div className="mt-8 pt-8 border-t border-gray-800 text-center text-sm text-gray-400">
            <p>&copy; {new Date().getFullYear()} {org?.name || 'Synagogue'}. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
